import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog"
import { Plus } from 'lucide-react'

interface APIConfig {
  id: string
  name: string
  url: string
  api_key: string
  api_secret: string | null
  type: 'search' | 'itinerary' | 'chatgpt'
  is_active: boolean
}

export default function APIConfigPage() {
  const [configs, setConfigs] = useState<APIConfig[]>([])
  const [editingConfig, setEditingConfig] = useState<APIConfig | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadConfigs()
  }, [])

  async function loadConfigs() {
    try {
      const { data, error } = await supabase
        .from('travel_api_configs')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setConfigs(data || [])
    } catch (error) {
      console.error('Failed to load API configs:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingConfig) return

    const form = e.target as HTMLFormElement
    const formData = new FormData(form)

    try {
      const { error } = await supabase
        .from('travel_api_configs')
        .update({
          name: formData.get('name'),
          url: formData.get('url'),
          api_key: formData.get('api_key'),
          api_secret: formData.get('api_secret'),
          type: formData.get('type'),
          is_active: formData.get('is_active') === 'true'
        })
        .eq('id', editingConfig.id)

      if (error) throw error
      
      await loadConfigs()
      setEditingConfig(null)
    } catch (error) {
      console.error('Failed to update API config:', error)
      alert('Failed to update API configuration')
    }
  }

  if (loading) {
    return <div>Loading API configurations...</div>
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold">API Configuration</h1>
        <Button onClick={() => setIsCreating(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add API
        </Button>
      </div>
      
      <div className="grid gap-6">
        {configs.map((config) => (
          <Card key={config.id}>
            <CardHeader>
              <CardTitle className="flex justify-between items-center">
                <span>{config.name}</span>
                <Button
                  variant="outline"
                  onClick={() => setEditingConfig(config)}
                >
                  Edit
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">URL:</span>
                  <span className="font-mono">{config.url}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type:</span>
                  <span className="capitalize">{config.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">API Key:</span>
                  <span className="font-mono">
                    {config.api_key.substring(0, 8)}...
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className={config.is_active ? "text-green-600" : "text-red-600"}>
                    {config.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New API</DialogTitle>
          </DialogHeader>
          <form onSubmit={async (e) => {
            e.preventDefault()
            const form = e.target as HTMLFormElement
            const formData = new FormData(form)

            try {
              const { error } = await supabase
                .from('travel_api_configs')
                .insert({
                  name: formData.get('name'),
                  url: formData.get('url'),
                  api_key: formData.get('api_key'),
                  api_secret: formData.get('api_secret'),
                  type: formData.get('type'),
                  is_active: formData.get('is_active') === 'true'
                })

              if (error) throw error
              
              await loadConfigs()
              setIsCreating(false)
            } catch (error) {
              console.error('Failed to create API config:', error)
              alert('Failed to create API configuration')
            }
          }}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="Enter API name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="url">URL</Label>
                <Input
                  id="url"
                  name="url"
                  placeholder="Enter API URL"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="api_key">API Key</Label>
                <Input
                  id="api_key"
                  name="api_key"
                  type="password"
                  placeholder="Enter API key"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="api_secret">API Secret (Optional)</Label>
                <Input
                  id="api_secret"
                  name="api_secret"
                  type="password"
                  placeholder="Enter API secret"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">API Type</Label>
                <select
                  id="type"
                  name="type"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  required
                >
                  <option value="search">Search API</option>
                  <option value="itinerary">Itinerary API</option>
                  <option value="chatgpt">ChatGPT API</option>
                  <option value="llm_query">LLM Query API</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="is_active">Status</Label>
                <select
                  id="is_active"
                  name="is_active"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  required
                >
                  <option value="false">Inactive</option>
                  <option value="true">Active</option>
                </select>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsCreating(false)}>
                Cancel
              </Button>
              <Button type="submit">Add API</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingConfig} onOpenChange={() => setEditingConfig(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit API Configuration</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave}>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  defaultValue={editingConfig?.name}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="url">URL</Label>
                <Input
                  id="url"
                  name="url"
                  defaultValue={editingConfig?.url}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="api_key">API Key</Label>
                <Input
                  id="api_key"
                  name="api_key"
                  type="password"
                  defaultValue={editingConfig?.api_key}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="api_secret">API Secret (Optional)</Label>
                <Input
                  id="api_secret"
                  name="api_secret"
                  type="password"
                  defaultValue={editingConfig?.api_secret || ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">API Type</Label>
                <select
                  id="type"
                  name="type"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  defaultValue={editingConfig?.type}
                  required
                >
                  <option value="search">Search API</option>
                  <option value="itinerary">Itinerary API</option>
                  <option value="chatgpt">ChatGPT API</option>
                  <option value="llm_query">LLM Query API</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="is_active">Status</Label>
                <select
                  id="is_active"
                  name="is_active"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  defaultValue={editingConfig?.is_active.toString()}
                  required
                >
                  <option value="false">Inactive</option>
                  <option value="true">Active</option>
                </select>
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setEditingConfig(null)}>
                Cancel
              </Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}